// Page Path : src\pages\design\document\configuration\right-pane.tsx
import { RootState } from 'store';
import { Box, CircularProgress, Dialog, Stack, Alert } from '@mui/material';
import CanvasToolbar from './toolbar';
import useFabricCanvas from 'hooks/useFabricCanvas';
import { FormikProps } from 'formik';
import { forwardRef, MutableRefObject, useEffect, useRef, useState } from 'react';
import { CloseOutlined, DoneOutlined, InfoOutlined } from '@mui/icons-material';
import { Tooltip } from 'components/elements';
import { useDispatch, useSelector } from 'react-redux';
import FabricImageCanvas from './canvas';
import {
  CavseRefType,
  GetFileCommentsResponse,
  GetFileHeaderDataResponse,
  ImageViewerGetDocumentBytesResponse,
  ImageViewerGetFileLevelResponse,
  ImageViwerNavigateFile,
  RectMetricsForRubberBandType,
  SearchTextInFileResponse,
  TechniqueNameListTypes
} from 'types/image-viewer';
import IconButtonExtended from 'components/@extended/IconButton';
import { generateMetricsFromROI, generateROIStringFromMetrics } from 'utils/imageViewerUtils';
import AssuranceAddPatternDialog from './AssuranceAddPatternDialog';
import { useLazyGetTextForROIQuery } from 'api/document';
import { Titlebar } from './TitleBar';
import useScreenSize from 'hooks/useScreenSize';
import { setRoiName } from 'store/reducers/columnSlice';
import { useLocation } from 'react-router';
import { setAddPatternRubberBand } from 'store/reducers/taggingSlice';

const translate = (key: string, options?: any) => (window as any).translate(key, options);

const ScrollNavigator = ({
  onScrollUp,
  onScrollRight,
  onScrollDown,
  onScrollLeft
}: {
  onScrollUp: () => void;
  onScrollRight: () => void;
  onScrollDown: () => void;
  onScrollLeft: () => void;
}) => {
  return (
    <Box className="canvas-scroll-navigator">
      <Box className="scroll-button-wrapper">
        <Box className="scroll-button scroll-up" onClick={onScrollUp} />
        <Box className="scroll-button scroll-right" onClick={onScrollRight} />
        <Box className="scroll-button scroll-down" onClick={onScrollDown} />
        <Box className="scroll-button scroll-left" onClick={onScrollLeft} />
      </Box>
    </Box>
  );
};

interface Props {
  fileHeaderData: GetFileHeaderDataResponse['headerData'];
  isSecondaryScreen?: boolean;
  currentPageNumber: number;
  setCurrentPageNumber: (pageNumber: number) => void;
  currentFileId: number; // Add current file ID state
  dualScreenWindowRef?: MutableRefObject<Window | null>;
  currentPageDetails?: ImageViewerGetDocumentBytesResponse;
  isLoadingImageBytes?: boolean;
  fileLevelDetails?: ImageViewerGetFileLevelResponse;
  onViewPdfClick?: () => void;
  searchTextInFile: (_value: string) => Promise<any[]>;
  fileComments?: GetFileCommentsResponse[];
  canvasFunctionRef?: { current: CavseRefType };
  updateFieldROI: (_a: string, _b?: number) => void;
  handleFileNavigation: ImageViwerNavigateFile;
  selectedAddPattern: TechniqueNameListTypes | null;
  selectedFieldName: string;
  handleAddNewPattern: any;
  expanded?: boolean;
  shouldRestrictModification?: boolean;
  clearSelectedAddPattern: () => void;
  setIsRighpanelImageLoading: (imagestate: boolean) => void;
  setExportLoader: (LoaderState:boolean) => void;
}

const ImageViewerRightPane = forwardRef<HTMLElement, Props>(
  (
    {
      fileHeaderData,
      isSecondaryScreen,
      currentPageNumber,
      setCurrentPageNumber,
      currentFileId, // Add the current file ID
      dualScreenWindowRef,
      currentPageDetails,
      isLoadingImageBytes,
      fileLevelDetails,
      onViewPdfClick,
      searchTextInFile,
      fileComments,
      canvasFunctionRef,
      updateFieldROI,
      handleFileNavigation,
      selectedAddPattern,
      selectedFieldName,
      handleAddNewPattern,
      expanded,
      shouldRestrictModification,
      clearSelectedAddPattern,
      setIsRighpanelImageLoading,
      setExportLoader
    },
    ref
  ) => {
    const patternFormRef = useRef<FormikProps<any> | null>(null);
    const searchTextRubberBandCoordsRef = useRef<RectMetricsForRubberBandType | null>(null);
    const [newRoi, setNewRoi] = useState<string>('');
    const [triggerGetTextForROI] = useLazyGetTextForROIQuery(); // Lazy query for flexibility
    const [addInsertInitialData, setAddInsertInitialData] = useState<any>(false);
    const [addEditPatternIsSaving, setAddEditPatternIsSaving] = useState<boolean>(false);
    const [showDialog, setshowDialog] = useState<boolean>(false);
    const [activeToolbarElement, setActiveToolbarElement] = useState<any>(null);
    const { isDesktop } = useScreenSize();
    const [fileNavigation, setFileNavigation] = useState<boolean>(false);
    const {
      canvasRef,
      onRotateLeft,
      onRotateRight,
      onZoomIn,
      onZoomOut,
      onRubberbandSelect,
      onRubberbandUnSelect,
      onFitToScreenSelect,
      onFitToScreenUnSelect,
      showRubberbandDialog,
      setShowRubberbandDialog,
      rubberbandDialogPosition,
      onDualScreen,
      dualScreenEnabled,
      currentZoomLevel,
      isMaxZoomIn,
      isMaxZoomOut,
      imageLoading,
      initCanvas,
      loadImage,
      setShowLabelRubberbandDialog,
      showLabelRubberbandDialog,
      disableLabelRubberband,
      isLabelRubberbandEnabled,
      highlightRubberBand,
      onRubberbandDialogDismiss,
      rubberbandMetrics,
      rectImageContent,
      fitToScreen,
      enableRubberBand
    } = useFabricCanvas({
      currentPageNumber,
      dualScreenWindowRef,
      secondaryScreenUrl: `image-viewer-secondary-view/${fileLevelDetails?.batchID}`,
      shouldCaptureRectImgContent: true,
      expanded
    });
    const location = useLocation();

    useEffect(() => {
      setIsRighpanelImageLoading(imageLoading);
    }, [imageLoading]);

    useEffect(() => {
      if (canvasFunctionRef) {
        canvasFunctionRef.current = {
          highlightRubberBand
        };
      }
    }, [canvasFunctionRef, highlightRubberBand]);

    const handleOnHighlightRubberBandBySearch = (data?: SearchTextInFileResponse) => {
      if (data) {
        const _metric: RectMetricsForRubberBandType = {
          left: data.xaxis,
          top: data.yaxis,
          width: data.width,
          height: data.height,
          pageNo: data.pageNo
        };
        if (data.pageNo !== currentPageNumber) {
          setCurrentPageNumber(data.pageNo);
          searchTextRubberBandCoordsRef.current = _metric;
        } else {
          highlightRubberBand(_metric);
        }
      } else {
        highlightRubberBand(); // Remove existing
      }
    };

    const canvasWrapperRef = useRef<HTMLElement>(null);
    const currentFieldLabel = useSelector((state: RootState) => state.taggingSlice.currentFieldLabel);
    const dispatch = useDispatch();
    useEffect(() => {
      initCanvas();
    }, [initCanvas]);

    useEffect(() => {
      searchTextRubberBandCoordsRef.current = null;
    }, [fileLevelDetails]);

    useEffect(() => {
      if (!isLoadingImageBytes && currentPageDetails) {
        const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
        canvasContainer?.scrollTo({ top: 0, left: 0 });
        loadImage(
          currentPageDetails.pageBytes,
          currentPageNumber,
          searchTextRubberBandCoordsRef.current?.pageNo === currentPageNumber ? searchTextRubberBandCoordsRef.current : undefined
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageDetails, isLoadingImageBytes, loadImage]);

    const onScrollUp = () => {
      const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
      canvasContainer?.scrollTo({ top: canvasContainer.scrollTop - 64, behavior: 'smooth' });
    };

    const onScrollRight = () => {
      const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
      canvasContainer?.scrollTo({ left: canvasContainer.scrollLeft + 150, behavior: 'smooth' });
    };

    const onScrollDown = () => {
      const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
      canvasContainer?.scrollTo({ top: canvasContainer.scrollTop + 64, behavior: 'smooth' });
    };

    const onScrollLeft = () => {
      const canvasContainer = canvasWrapperRef.current?.getElementsByClassName('canvas-container')[0];
      canvasContainer?.scrollTo({ left: canvasContainer.scrollLeft - 150, behavior: 'smooth' });
    };

    const onConfirmUpdateFieldROI = () => {
      if (rubberbandMetrics) {
        const newROI = generateROIStringFromMetrics(rubberbandMetrics);
        updateFieldROI(newROI, currentPageNumber);
      }
      onRubberbandDialogDismiss();
    };

    useEffect(() => {
      if (selectedAddPattern) {
        onRubberbandSelect();
      } else {
        onRubberbandUnSelect();
      }
    }, [onRubberbandSelect, onRubberbandUnSelect, selectedAddPattern]);

    const onCancelAddpattern = () => {
      onRubberbandDialogDismiss();
      clearSelectedAddPattern();
      dispatch(setAddPatternRubberBand(false));
    };

    const onSubmitAddpattern = async () => {
      try {
        if (!patternFormRef.current) return;
        setAddEditPatternIsSaving(true);
        const values: any = patternFormRef.current.values || {};
        await handleAddNewPattern({
          patternROI: newRoi,
          ...values
        });
        if (activeToolbarElement !== 'rubber-band') {
          onRubberbandUnSelect();
        }
        setNewRoi('');
        onRubberbandDialogDismiss();
        console.log(newRoi);
      } catch (e) {
        console.error(e);
      } finally {
        setAddEditPatternIsSaving(false);
        dispatch(setAddPatternRubberBand(false));
      }
    };

    const setAddPatternInitialData = async () => {
      if (rubberbandMetrics) {
        const newROI = generateROIStringFromMetrics(rubberbandMetrics);
        setNewRoi(newROI);
        const coordinates = generateMetricsFromROI(newROI);
        const { data: roiData }: any = await triggerGetTextForROI({
          FileId: currentFileId,
          PageNo: currentPageNumber,
          Xaxis: coordinates?.left,
          Yaxis: coordinates?.top,
          Width: coordinates?.width,
          Height: coordinates?.height
        });
        setAddInsertInitialData({ patternTextFromROI: roiData?.data?.text });
        dispatch(setRoiName(roiData?.text));
      }
    };

    useEffect(() => {
      if (rubberbandMetrics && selectedAddPattern && !isNaN(rubberbandMetrics.startX)) {
        setAddPatternInitialData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rubberbandMetrics, selectedAddPattern]);

    useEffect(() => {
      if (shouldRestrictModification) {
        onRubberbandUnSelect();
      }
    }, [shouldRestrictModification]);
    const firstPart = selectedAddPattern?.techniqueName
      ? selectedAddPattern.techniqueName.includes('Nested Lookup Printed')
        ? 'NestedLookupPrinted'
        : selectedAddPattern.techniqueName.includes('Nested - Keyvaluepair printed')
        ? 'NestedKeyvaluepairprinted'
        : selectedAddPattern.techniqueName.toLowerCase().includes('lookup printed')
        ? 'Lookup'
        : selectedAddPattern.techniqueName.toUpperCase().includes('KEYVALUEPAIR')
        ? 'KeyValuePair'
        : selectedAddPattern.techniqueName.split('-')[0]?.trim()
      : '';

    return (
      <Stack flexDirection="column" sx={{ height: '100%', width: '100%' }}>
        {isDesktop && (
          <Titlebar
            currentFileId={currentFileId} // Pass the current file ID
            fileLevelDetails={fileLevelDetails}
            onViewPdfClick={onViewPdfClick}
            fileComments={fileComments}
            handleFileNavigation={handleFileNavigation}
            isSecondaryScreen={isSecondaryScreen}
            shouldRestrictModification={Boolean(shouldRestrictModification)}
            setFileNavigation={setFileNavigation}
            setExportLoader={setExportLoader}
          />
        )}
        <Box className="right-pane-content">
          {!dualScreenEnabled && (
            <CanvasToolbar
              onRotateLeft={onRotateLeft}
              onRotateRight={onRotateRight}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
              onRubberbandSelect={onRubberbandSelect}
              onRubberbandUnSelect={onRubberbandUnSelect}
              onFitToScreenSelect={onFitToScreenSelect}
              onFitToScreenUnSelect={onFitToScreenUnSelect}
              onDualScreen={onDualScreen}
              isSecondaryScreen={isSecondaryScreen}
              currentZoomLevel={currentZoomLevel}
              isMaxZoomIn={isMaxZoomIn}
              isMaxZoomOut={isMaxZoomOut}
              currentPage={currentPageNumber}
              onPageNumberChange={setCurrentPageNumber}
              totalPages={fileLevelDetails?.totalPages ?? 0}
              searchTextInFile={searchTextInFile}
              onSelectSearchTextInFile={handleOnHighlightRubberBandBySearch as any}
              fitToScreen={fitToScreen}
              disableRubberband={Boolean(shouldRestrictModification) || location.state?.isFromDPC}
              setActiveToolbarElement={setActiveToolbarElement}
              fileNavigation={fileNavigation}
              setFileNavigation={setFileNavigation}
              imageLoading={imageLoading}
              enableRubberBand={enableRubberBand}
              fileHeaderData={fileHeaderData}
            />
          )}
          {isLabelRubberbandEnabled && (
            <Box
              className="configuration-lable-enable"
              sx={{
                position: 'absolute', // Ensure it's overlayed
                top: '55px', // 30px from the top
                left: '50%', // Center horizontally
                transform: 'translateX(-50%)', // Adjust to center properly
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 150
              }}
            >
              <Alert color="secondary" className="configuration-lable-enable-alert" icon={<InfoOutlined />}>
                {translate('pages.review.assurance.labels.pleaseRubberband')}{' '}
                <strong>{currentFieldLabel || translate('pages.review.assurance.labels.label')}</strong>{' '}
                {translate('pages.review.assurance.labels.toContinue')}
              </Alert>
            </Box>
          )}
          {!fitToScreen && (
            <ScrollNavigator
              onScrollUp={onScrollUp}
              onScrollRight={onScrollRight}
              onScrollDown={onScrollDown}
              onScrollLeft={onScrollLeft}
            />
          )}
          <Box className="canvas-wrapper" ref={canvasWrapperRef}>
            <FabricImageCanvas canvasRef={canvasRef} />
            {(isLoadingImageBytes || imageLoading) && (
              <Box className="configuration-loader-wrapper">
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>
        {!selectedAddPattern ? (
          <Dialog
            open={showRubberbandDialog}
            onClose={() => {
              setShowLabelRubberbandDialog(false);
              disableLabelRubberband();
            }}
            BackdropProps={{
              style: {
                background: 'transparent'
              }
            }}
            PaperProps={{
              style: {
                position: 'absolute',
                margin: 0,
                padding: '5px',
                left: `${rubberbandDialogPosition?.left}px`,
                top: `${rubberbandDialogPosition?.top}px`,
                borderRadius: '8px',
                border: '1px solid #9E9E9D',
                background: '#E2E2E2',
                boxShadow: '0px 4px 8px 0px rgba(158, 158, 157, 0.50)'
              }
            }}
          >
            <Box sx={{ padding: '5px' }}>
              <Tooltip title={translate('pages.review.assurance.labels.add')}>
                <IconButtonExtended shape="rounded" color="success" onClick={onConfirmUpdateFieldROI}>
                  <DoneOutlined />
                </IconButtonExtended>
              </Tooltip>
              <Tooltip title={translate('pages.review.assurance.labels.clear')}>
                <IconButtonExtended shape="rounded" color="error" onClick={onRubberbandDialogDismiss}>
                  <CloseOutlined />
                </IconButtonExtended>
              </Tooltip>
            </Box>
          </Dialog>
        ) : (
          <>
            <AssuranceAddPatternDialog
              open={showRubberbandDialog}
              addEditPatternType="ADD"
              fileName={fileLevelDetails?.fileName || ''}
              setshowDialog={setshowDialog}
              addInsertInitialData={addInsertInitialData}
              rectImageContent={rectImageContent || ''}
              onCancel={onCancelAddpattern}
              isSubmitLoading={addEditPatternIsSaving}
              onSubmit={onSubmitAddpattern}
              selectedFieldName={selectedFieldName}
              selectedTechniqueName={firstPart}
              currentPageNumber={currentPageNumber}
              setShowLabelRubberbandDialog={setShowRubberbandDialog}
              patternFormRef={patternFormRef}
              disableLabelRubberband={onRubberbandDialogDismiss}
            />
            {showDialog && (
              <>
                <Dialog
                  open={showLabelRubberbandDialog}
                  onClose={() => {
                    setShowLabelRubberbandDialog(false);
                    disableLabelRubberband();
                  }}
                  BackdropProps={{
                    style: {
                      background: 'transparent'
                    }
                  }}
                  PaperProps={{
                    style: {
                      position: 'absolute',
                      margin: 0,
                      padding: '5px',
                      left: `${rubberbandDialogPosition?.left}px`,
                      top: `${rubberbandDialogPosition?.top}px`,
                      borderRadius: '8px',
                      border: '1px solid #9E9E9D',
                      background: '#E2E2E2',
                      boxShadow: '0px 4px 8px 0px rgba(158, 158, 157, 0.50)'
                    }
                  }}
                >
                  <Box sx={{ padding: '5px' }}>
                    <Tooltip title={translate('pages.review.assurance.labels.add')}>
                      <IconButtonExtended
                        shape="rounded"
                        color="success"
                        onClick={() => {
                          setShowRubberbandDialog(true);
                          setShowLabelRubberbandDialog(false);
                          disableLabelRubberband();
                        }}
                      >
                        <DoneOutlined />
                      </IconButtonExtended>
                    </Tooltip>
                    <Tooltip title={translate('pages.review.assurance.labels.clear')}>
                      <IconButtonExtended
                        shape="rounded"
                        color="error"
                        onClick={() => {
                          disableLabelRubberband();
                          setShowLabelRubberbandDialog(false);
                        }}
                      >
                        <CloseOutlined />
                      </IconButtonExtended>
                    </Tooltip>
                  </Box>
                </Dialog>
              </>
            )}
          </>
        )}
      </Stack>
    );
  }
);

export default ImageViewerRightPane;
